import { useCallback, useEffect, useRef, useState } from 'react';
import { labelsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Printer, Settings2, Pencil, Tag, RotateCcw, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const todayIso = () => new Date().toISOString().slice(0, 10);
import LabelAdjusterDialog from './LabelAdjuster';

const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return typeof d === 'string' ? d : fallback;
};

export default function LabelsView({ clients }) {
    const { user, hasPermission } = useAuth();
    const isAdmin = user?.role === 'admin';
    const canPrint = isAdmin || hasPermission('labels_print');
    const [clientId, setClientId] = useState(clients[0]?.id);
    const [date, setDate] = useState(todayIso());
    const [rows, setRows] = useState([]);
    const [qty, setQty] = useState({});
    const [checked, setChecked] = useState({});
    const [loading, setLoading] = useState(false);
    const [previewId, setPreviewId] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [previewErr, setPreviewErr] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [agents, setAgents] = useState([]);
    const [printer, setPrinter] = useState('');
    const [jobs, setJobs] = useState([]);
    const [printing, setPrinting] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const [dataRes, qtyRes, tplRes, agRes, jobsRes] = await Promise.all([
                labelsAPI.data(clientId),
                labelsAPI.dayQuantities(clientId, date),
                labelsAPI.templates(),
                labelsAPI.agents().catch(() => ({ data: [] })),
                labelsAPI.jobs().catch(() => ({ data: [] })),
            ]);
            setRows(dataRes.data);
            setTemplates(tplRes.data);
            setAgents(agRes.data);
            setJobs(jobsRes.data.slice(0, 5));
            const q = {}, ck = {};
            dataRes.data.forEach(p => {
                q[p.id] = qtyRes.data[p.id] || 0;
                ck[p.id] = (qtyRes.data[p.id] || 0) > 0 && !!p.label?.template_id;
            });
            setQty(q);
            setChecked(ck);
            setPreviewId(null);
            setPreviewSrc(null);
            setPreviewErr(null);
        } catch { toast.error('Erreur de chargement des étiquettes'); }
        setLoading(false);
    }, [clientId, date]);
    useEffect(() => { load(); }, [load]);

    // Rafraîchir l'état des agents régulièrement : le heartbeat arrive toutes les 5 s,
    // sans ce polling le bandeau « hors ligne » devenait faux après 60 s de page ouverte.
    useEffect(() => {
        const t = setInterval(async () => {
            try {
                const [ag, jb] = await Promise.all([labelsAPI.agents(), labelsAPI.jobs()]);
                setAgents(ag.data);
                setJobs(jb.data.slice(0, 5));
            } catch { /* ignore */ }
        }, 20000);
        return () => clearInterval(t);
    }, []);

    const showPreview = async (pid) => {
        setPreviewId(pid);
        setPreviewSrc(null);
        setPreviewErr(null);
        try {
            const res = await labelsAPI.previewBlob(clientId, pid, date);
            setPreviewSrc(URL.createObjectURL(res.data));
        } catch (e) {
            let txt = null;
            try {
                if (e.response?.data instanceof Blob) {
                    const d = JSON.parse(await e.response.data.text())?.detail;
                    txt = typeof d === 'string' ? d : null;
                }
            } catch { /* ignore */ }
            const row = rows.find(r => r.id === pid);
            const hasTpl = !!(row?.label?.template_id);
            setPreviewErr(txt || (!hasTpl
                ? 'Ce produit n\'a pas de modèle d\'étiquette associé. Ouvrez le crayon ✏️ pour choisir le modèle, ou associez-le via Gérer → lignes non associées.'
                : 'Aperçu impossible — vérifiez les données du produit (crayon ✏️).'));
        }
    };

    const doPrint = async () => {
        const sel = rows.filter(r => checked[r.id] && Number(qty[r.id]) > 0);
        const noTpl = sel.filter(r => !r.label?.template_id);
        const items = sel.filter(r => r.label?.template_id).map(r => ({ product_id: r.id, qty: Number(qty[r.id]) }));
        if (noTpl.length) toast.warning(`Sans modèle d'étiquette, non imprimés : ${noTpl.map(r => r.name).join(', ')}`, { duration: 7000 });
        if (!items.length) return toast.error('Aucun produit coché avec quantité et modèle d\'étiquette');
        setPrinting(true);
        try {
            const res = await labelsAPI.print({ client_id: clientId, date, printer: printer || null, items });
            toast.success(`Lot envoyé : ${res.data.total} étiquette(s) en file d'impression`);
            setJobs((await labelsAPI.jobs()).data.slice(0, 5));
        } catch (e) { toast.error(errMsg(e, 'Erreur impression')); }
        setPrinting(false);
    };

    const totalLabels = rows.filter(r => checked[r.id]).reduce((n, r) => n + (Number(qty[r.id]) || 0), 0);
    const printers = [...new Set(agents.flatMap(a => a.printers || []))];
    const client = clients.find(c => c.id === clientId);
    const tplName = (id) => templates.find(t => t.id === id)?.name?.replace('Etiquette - ', '') || '—';

    return (
        <div className="space-y-3" data-testid="labels-view">
            <div className="flex items-center gap-2 flex-wrap">
                {clients.map(c => (
                    <button key={c.id} onClick={() => setClientId(c.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${clientId === c.id ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid={`labels-client-${c.code}`}>{c.code}</button>
                ))}
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8 w-40" data-testid="labels-date-input" />
                {printers.length > 0 && (
                    <Select value={printer} onValueChange={setPrinter}>
                        <SelectTrigger className="h-8 w-56" data-testid="labels-printer-select"><SelectValue placeholder="Imprimante (auto)" /></SelectTrigger>
                        <SelectContent>{printers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                )}
                {isAdmin && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setAdminOpen(true)} data-testid="labels-admin-btn">
                        <Settings2 className="w-4 h-4 mr-1" />Gérer
                    </Button>
                )}
                {canPrint && (
                    <Button size="sm" className="h-8 ml-auto bg-teal-700 hover:bg-teal-800" onClick={doPrint}
                        disabled={printing || totalLabels === 0} data-testid="labels-print-btn">
                        {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
                        Imprimer {totalLabels} étiquette{totalLabels > 1 ? 's' : ''}
                    </Button>
                )}
            </div>
            {(() => {
                const paired = agents.filter(a => a.paired);
                if (paired.length === 0) return (
                    <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                        Aucun agent d'impression appairé — l'impression restera « en attente » jusqu'à ce que l'agent du PC labo soit installé et connecté (bouton Gérer).
                    </p>
                );
                const online = paired.filter(a => a.last_seen && (Date.now() - new Date(a.last_seen).getTime()) < 60000);
                if (online.length === 0) {
                    const last = paired.map(a => a.last_seen).filter(Boolean).sort().pop();
                    return (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="labels-agent-offline">
                            ⚠ Agent d'impression hors-ligne{last ? ` (dernier contact : ${new Date(last).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})` : ''} — les lots envoyés restent « En attente ». Relancez l'agent sur le PC du labo (installer_sokuzai.bat).
                        </p>
                    );
                }
                return null;
            })()}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2"><CardContent className="p-0">
                    {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
                        <div className="overflow-y-auto max-h-[62vh]" data-testid="labels-table-scroll">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b sticky top-0 z-10">
                                <tr className="text-[10px] uppercase text-slate-500">
                                    <th className="px-2 py-1.5 w-8">
                                        <input type="checkbox" className="h-4 w-4 accent-teal-700 align-middle"
                                            checked={rows.length > 0 && rows.every(r => checked[r.id])}
                                            ref={el => { if (el) el.indeterminate = rows.some(r => checked[r.id]) && !rows.every(r => checked[r.id]); }}
                                            onChange={e => {
                                                const v = e.target.checked;
                                                setChecked(c => ({ ...c, ...Object.fromEntries(rows.map(r => [r.id, v])) }));
                                            }}
                                            title="Tout cocher / décocher"
                                            data-testid="labels-check-all" />
                                    </th>
                                    <th className="px-2 py-1.5 text-left">Produit</th>
                                    <th className="px-2 py-1.5 text-left w-32">Modèle</th>
                                    <th className="px-2 py-1.5 w-14">DLC (j)</th>
                                    <th className="px-2 py-1.5 w-20">Qté</th>
                                    <th className="px-2 py-1.5 w-14"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id} className={`border-b border-slate-100 cursor-pointer ${previewId === r.id ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                                        onClick={() => showPreview(r.id)} data-testid={`labels-row-${r.id}`}>
                                        <td className="px-2 py-1 text-center" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" className="h-4 w-4 accent-teal-700" checked={!!checked[r.id]}
                                                onChange={e => setChecked(c => ({ ...c, [r.id]: e.target.checked }))}
                                                data-testid={`labels-check-${r.id}`} />
                                        </td>
                                        <td className="px-2 py-1 font-medium">{r.name}</td>
                                        <td className="px-2 py-1 text-slate-500 max-w-[130px]">
                                            <span className="flex items-center gap-1">
                                                <span className="truncate">{tplName(r.label?.template_id)}</span>
                                                {r.label?.adjustments && Object.keys(r.label.adjustments).length > 0 && (
                                                    <span className="shrink-0 px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[9px] font-semibold" title="Ajustement spécifique à ce produit" data-testid={`labels-adjusted-badge-${r.id}`}>ajusté</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1 text-center text-slate-500">{r.label?.shelf_life_days ?? '—'}</td>
                                        <td className="px-2 py-1" onClick={e => e.stopPropagation()}>
                                            <Input type="number" min="0" value={qty[r.id] ?? 0}
                                                onChange={e => {
                                                    setQty(q => ({ ...q, [r.id]: e.target.value }));
                                                    if (Number(e.target.value) > 0) setChecked(c => ({ ...c, [r.id]: true }));
                                                }}
                                                className="h-7 text-center text-xs" data-testid={`labels-qty-${r.id}`} />
                                        </td>
                                        <td className="px-2 py-1 text-center" onClick={e => e.stopPropagation()}>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditRow(r)} data-testid={`labels-edit-${r.id}`}>
                                                <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </CardContent></Card>
                <div className="space-y-3">
                    <Card><CardContent className="p-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Aperçu (50×100 mm, 203 dpi)</p>
                        {previewSrc ? (
                            <img src={previewSrc} alt="Étiquette" className="border rounded shadow-sm mx-auto" style={{ width: 200 }} data-testid="labels-preview-img" />
                        ) : previewErr ? (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 text-center" data-testid="labels-preview-error">{previewErr}</p>
                        ) : (
                            <p className="text-xs text-slate-400 text-center py-10">{previewId ? 'Chargement…' : 'Cliquez sur un produit pour voir son étiquette exacte.'}</p>
                        )}
                    </CardContent></Card>
                    <Card><CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase">Derniers lots</p>
                            {jobs.length > 0 && (
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-400 hover:text-red-600" title="Purger la liste des lots"
                                    data-testid="labels-purge-jobs-btn"
                                    onClick={async () => {
                                        if (!window.confirm('Purger la liste des lots envoyés ? Les lots en attente seront aussi annulés.')) return;
                                        try {
                                            const res = await labelsAPI.purgeJobs();
                                            setJobs((await labelsAPI.jobs()).data.slice(0, 5));
                                            toast.success(`${res.data.deleted} lot(s) purgé(s)`);
                                        } catch { toast.error('Échec de la purge'); }
                                    }}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>
                        {jobs.length === 0 && <p className="text-xs text-slate-400">Aucun lot imprimé.</p>}
                        {jobs.map(j => (
                            <div key={j.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0" data-testid={`labels-job-${j.id}`}>
                                <span>{j.client_code} · {j.total} étiq. · {new Date(j.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="flex items-center gap-1">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${j.status === 'done' ? 'bg-emerald-100 text-emerald-700' : j.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {j.status === 'done' ? 'Imprimé' : j.status === 'error' ? 'Erreur' : j.status === 'printing' ? 'En cours' : 'En attente'}
                                    </span>
                                    {j.status === 'error' && (
                                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" title={j.error || ''}
                                            onClick={async () => { await labelsAPI.retryJob(j.id); setJobs((await labelsAPI.jobs()).data.slice(0, 5)); }}>
                                            <RotateCcw className="w-3 h-3" />
                                        </Button>
                                    )}
                                </span>
                            </div>
                        ))}
                    </CardContent></Card>
                </div>
            </div>
            {editRow && <EditLabelDialog row={editRow} clientId={clientId} templates={templates.filter(t => t.client_id === clientId)} products={rows} onClose={(saved) => { setEditRow(null); if (saved) load(); }} />}
            {adminOpen && <LabelsAdminDialog client={client} products={rows} templates={templates.filter(t => t.client_id === clientId)} agents={agents} onClose={() => { setAdminOpen(false); load(); }} />}
        </div>
    );
}

const FIELDS = [
    ['designation', 'Désignation'], ['description', 'Description'], ['allergenes', 'Allergènes'],
    ['ingredients', 'Ingrédients'], ['consigne', 'Consigne'], ['conservation', 'Conservation'],
    ['prix_kg', 'Prix Kg'], ['poids_net', 'Poids Net (g)'], ['prix_ttc', 'Prix TTC'], ['ean', 'EAN'], ['siege', 'Siège'], ['categorie', 'Catégorie'],
];

function EditLabelDialog({ row, clientId, templates, products = [], onClose }) {
    const [fields, setFields] = useState({ ...(row.label?.fields || {}) });
    const [shelf, setShelf] = useState(row.label?.shelf_life_days ?? '');
    const [tplId, setTplId] = useState(row.label?.template_id || '');
    const [saving, setSaving] = useState(false);
    const [adjustOpen, setAdjustOpen] = useState(false);
    const hasAdjust = row.label?.adjustments && Object.keys(row.label.adjustments).length > 0;
    const save = async () => {
        setSaving(true);
        try {
            await labelsAPI.saveData(clientId, row.id, { fields, shelf_life_days: shelf === '' ? null : Number(shelf), template_id: tplId || null });
            toast.success('Données étiquette enregistrées');
            onClose(true);
        } catch { toast.error('Erreur'); setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={() => onClose(false)}>
            <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="label-edit-dialog">
                <DialogHeader><DialogTitle className="text-base">Étiquette — {row.name}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Label className="text-xs">Modèle d'étiquette</Label>
                        <Select value={tplId} onValueChange={setTplId}>
                            <SelectTrigger className="h-8 text-xs" data-testid="label-template-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                            <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Durée de vie (jours) → DLC</Label>
                        <Input type="number" min="0" value={shelf} onChange={e => setShelf(e.target.value)} className="h-8 text-xs" data-testid="label-shelf-input" />
                    </div>
                    {FIELDS.map(([k, l]) => (
                        <div key={k} className={['ingredients', 'conservation', 'siege', 'description'].includes(k) ? 'col-span-2' : ''}>
                            <Label className="text-xs">{l}</Label>
                            {['ingredients', 'conservation', 'siege'].includes(k) ? (
                                <textarea value={fields[k] || ''} onChange={e => setFields(f => ({ ...f, [k]: e.target.value }))}
                                    className="w-full border rounded-md px-2 py-1 text-xs min-h-[52px]" data-testid={`label-field-${k}`} />
                            ) : (
                                <Input value={fields[k] || ''} onChange={e => setFields(f => ({ ...f, [k]: e.target.value }))} className="h-8 text-xs" data-testid={`label-field-${k}`} />
                            )}
                        </div>
                    ))}
                </div>
                <DialogFooter className="flex-row justify-between sm:justify-between">
                    <Button variant="ghost" size="sm" className="text-indigo-600 px-1" disabled={!tplId}
                        onClick={() => setAdjustOpen(true)} data-testid="label-adjust-product-btn">
                        Ajustement spécifique{hasAdjust ? ' ✓' : ''}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onClose(false)}>Annuler</Button>
                        <Button onClick={save} disabled={saving} data-testid="label-edit-save">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Enregistrer</Button>
                    </div>
                </DialogFooter>
                {adjustOpen && tplId && (
                    <LabelAdjusterDialog clientId={clientId} template={templates.find(t => t.id === tplId)}
                        products={products} scope="product" product={row}
                        onClose={(saved) => { setAdjustOpen(false); if (saved) onClose(true); }} />
                )}
            </DialogContent>
        </Dialog>
    );
}

function LabelsAdminDialog({ client, products = [], templates: initialTemplates, agents: initialAgents, onClose }) {
    const fileRef = useRef(null);
    const xlsRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [pairCode, setPairCode] = useState(null);
    const [xlsTpl, setXlsTpl] = useState('');
    const [assoc, setAssoc] = useState({});
    const [templates, setTemplates] = useState(initialTemplates);
    const [agents, setAgents] = useState(initialAgents);
    const [xlsImports, setXlsImports] = useState([]);
    const [adjustTpl, setAdjustTpl] = useState(null);

    const refreshLog = useCallback(() => {
        labelsAPI.importLog(client.id).then(r => setXlsImports(r.data?.imports || [])).catch(() => {});
    }, [client.id]);
    useEffect(() => { refreshLog(); }, [refreshLog]);

    const unmatched = [...new Map(xlsImports.flatMap(l => (l.not_matched || [])
        .map(e => (typeof e === 'string' ? { designation: e } : e))
        .map(e => [e.designation, { ...e, file: l.filename }])).map(x => [x[0], x[1]])).values()];

    const refreshTemplates = async () => {
        try { setTemplates((await labelsAPI.templates()).data.filter(t => t.client_id === client.id)); } catch { /* ignore */ }
    };
    const refreshAgents = async () => {
        try { setAgents((await labelsAPI.agents()).data); } catch { /* ignore */ }
    };

    const importNlbl = async (file) => {
        setBusy(true);
        try {
            const res = await labelsAPI.importTemplate(client.id, file);
            const un = res.data.unsupported || [];
            toast.success(`Modèle « ${res.data.template.name} » ${res.data.updated ? 'mis à jour' : 'importé'}${un.length ? ` — objets non reconnus : ${un.join(', ')}` : ''}`, { duration: 8000 });
            refreshTemplates();
        } catch (e) { toast.error(errMsg(e, 'Import impossible')); }
        setBusy(false);
    };
    const importXls = async (file) => {
        setBusy(true);
        try {
            const res = await labelsAPI.importExcel(client.id, xlsTpl || null, file);
            const nm = res.data.not_matched || [];
            const map = res.data.mapping || {};
            const prix = ['Prix TTC', 'Prix Kg'].map(k => `${k} ← ${map[k] ? `colonne « ${map[k]} »` : 'absent du fichier'}`).join(' · ');
            toast.success(`${res.data.imported} produit(s) mis à jour${nm.length ? ` — ${nm.length} non trouvé(s), à associer ci-dessous` : ''}`, { description: prix, duration: 10000 });
            refreshLog();
        } catch (e) { toast.error(errMsg(e, 'Import Excel impossible')); }
        setBusy(false);
    };
    const associate = async (designation) => {
        const pid = assoc[designation];
        if (!pid) return toast.error('Choisissez un produit');
        try {
            const r = await labelsAPI.associate({ client_id: client.id, designation, product_id: pid });
            if (r.data.template_set) {
                toast.success(`« ${designation} » associé — mémorisé pour les prochains imports`);
            } else {
                toast.warning(`« ${designation} » associé, mais sans modèle d'étiquette — choisissez le modèle via le crayon ✏️ du produit`, { duration: 8000 });
            }
            refreshLog();
        } catch (e) { toast.error(errMsg(e, 'Association impossible')); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="labels-admin-dialog">
                <DialogHeader>
                    <DialogTitle className="text-base">Gestion étiquettes — {client?.code}</DialogTitle>
                    <DialogDescription>Modèles NiceLabel, imports Excel et agent d'impression.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Modèles (.nlbl) de {client?.code}</p>
                        {templates.map(t => (
                            <div key={t.id} className="text-xs py-1 border-b border-slate-100">
                                <div className="flex items-center justify-between">
                                    <span>{t.name} <span className="text-slate-400">· {Object.keys(t.bindings || {}).length} variables</span>
                                        {t.has_adjustments && <span className="ml-1 px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[9px] font-semibold">ajusté</span>}
                                        {t.imported_at && <span className="block text-[10px] text-slate-400" data-testid={`labels-tpl-meta-${t.id}`}>{t.source_filename} · importé le {new Date(t.imported_at).toLocaleDateString('fr-FR')}</span>}
                                    </span>
                                    <span className="flex items-center shrink-0">
                                        <Button variant="ghost" size="sm" className="h-6 text-indigo-600 text-xs px-1.5" onClick={() => setAdjustTpl(t)} data-testid={`labels-adjust-btn-${t.id}`}>Ajuster</Button>
                                        <Button variant="ghost" size="sm" className="h-6 text-red-500 text-xs px-1.5" onClick={async () => {
                                            if (!window.confirm(`Supprimer le modèle « ${t.name} » ?\n\nATTENTION : ses ajustements visuels, logos et liaisons seront définitivement perdus.\nPour mettre à jour la maquette, utilisez plutôt « Importer / réimporter un .nlbl » : tout est conservé.`)) return;
                                            try { await labelsAPI.deleteTemplate(t.id); toast.success('Modèle supprimé'); refreshTemplates(); } catch (e) { toast.error(errMsg(e, 'Erreur')); }
                                        }}>Suppr.</Button>
                                    </span>
                                </div>
                                {(t.image_objects || []).map(io => (
                                    <div key={io.name} className="flex items-center gap-2 pl-3 mt-0.5">
                                        <span className={`text-[10px] ${io.uploaded ? 'text-emerald-600' : 'text-amber-600'}`}>
                                            {io.uploaded ? '✓' : '⚠'} Logo « {io.file || io.name} »{io.uploaded ? '' : ' — manquant'}
                                        </span>
                                        <label className="text-[10px] text-teal-700 underline cursor-pointer" data-testid={`labels-logo-upload-${t.id}-${io.name}`}>
                                            {io.uploaded ? 'remplacer' : 'télécharger'}
                                            <input type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" onChange={async e => {
                                                const f = e.target.files[0];
                                                e.target.value = '';
                                                if (!f) return;
                                                try {
                                                    await labelsAPI.uploadTemplateImage(t.id, io.name, f);
                                                    toast.success(`Logo « ${io.name} » enregistré`);
                                                    refreshTemplates();
                                                } catch (err) { toast.error(errMsg(err, 'Upload impossible')); }
                                            }} />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ))}
                        <input ref={fileRef} type="file" accept=".nlbl" className="hidden" onChange={e => { if (e.target.files[0]) importNlbl(e.target.files[0]); e.target.value = ''; }} data-testid="labels-nlbl-input" />
                        <Button variant="outline" size="sm" className="h-8 mt-2" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="labels-import-nlbl-btn">
                            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Importer / réimporter un .nlbl
                        </Button>
                        <p className="text-[11px] text-slate-400 mt-1">Réimporter un fichier du même nom met à jour le modèle en place : les produits associés et vos liaisons de variables sont conservés.</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Import Excel (données produits)</p>
                        <div className="flex items-center gap-2">
                            <Select value={xlsTpl} onValueChange={setXlsTpl}>
                                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Associer au modèle (optionnel)" /></SelectTrigger>
                                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                            <input ref={xlsRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={e => { if (e.target.files[0]) importXls(e.target.files[0]); e.target.value = ''; }} data-testid="labels-xls-input" />
                            <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => xlsRef.current?.click()} data-testid="labels-import-xls-btn">Importer .xls</Button>
                        </div>
                        {xlsImports.map(l => (
                            <p key={l.filename} className="text-[11px] text-slate-500 mt-1" data-testid="labels-xls-last-import">
                                {l.filename} · {new Date(l.at).toLocaleDateString('fr-FR')} · {l.imported} produit(s){l.not_matched?.length ? ` · ${l.not_matched.length} non trouvé(s)` : ''}
                            </p>
                        ))}
                        {unmatched.length > 0 && (
                            <div className="mt-2 border border-amber-200 bg-amber-50/60 rounded p-2 space-y-1.5" data-testid="labels-unmatched-list">
                                <p className="text-[11px] font-semibold text-amber-800">Lignes Excel non associées — choisissez le produit correspondant :</p>
                                {unmatched.map((e, i) => {
                                    const desig = e.designation;
                                    return (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="text-[11px] flex-1 truncate" title={`${desig} (${e.file || ''})`}>{desig}</span>
                                            <Select value={assoc[desig] || ''} onValueChange={v => setAssoc(a => ({ ...a, [desig]: v }))}>
                                                <SelectTrigger className="h-7 text-xs w-48" data-testid={`labels-assoc-select-${i}`}><SelectValue placeholder="Produit…" /></SelectTrigger>
                                                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                                            </Select>
                                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => associate(desig)} data-testid={`labels-assoc-btn-${i}`}>Associer</Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">Actualiser les tarifs = réimporter uniquement le .xls, sans toucher au .nlbl : maquette, ajustements et logos restent en place. Le choix « Associer au modèle » ne s'applique qu'aux produits qui n'ont pas encore de modèle. Durée de vie et modèles choisis manuellement sont conservés.</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Agent d'impression (PC labo)</p>
                        {agents.map(a => (
                            <div key={a.id} className="text-xs py-1 border-b border-slate-100 flex items-center justify-between">
                                <span>
                                    {a.paired && <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${a.last_seen && (Date.now() - new Date(a.last_seen).getTime()) < 60000 ? 'bg-emerald-500' : 'bg-red-500'}`} title={a.last_seen && (Date.now() - new Date(a.last_seen).getTime()) < 60000 ? 'En ligne' : 'Hors-ligne'} />}
                                    {a.paired ? `${a.name} — ${(a.printers || []).length} imprimante(s)` : `Code : ${a.pairing_code} (en attente)`}
                                    {a.paired && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded font-semibold ${a.version ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`} data-testid="agent-version-badge">{a.version ? `v${a.version}` : 'version ancienne'}</span>}
                                    {a.last_seen && <span className="text-slate-400"> · vu {new Date(a.last_seen).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                                </span>
                                <Button variant="ghost" size="sm" className="h-6 text-red-500 text-xs" onClick={async () => {
                                    if (!window.confirm('Supprimer cet agent d\'impression ?')) return;
                                    try { await labelsAPI.deleteAgent(a.id); toast.success('Agent supprimé'); refreshAgents(); } catch (e) { toast.error(errMsg(e, 'Erreur')); }
                                }}>Suppr.</Button>
                            </div>
                        ))}
                        {pairCode && <p className="text-sm font-bold text-teal-700 my-1" data-testid="labels-pair-code">Code d'appairage : {pairCode}</p>}
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <Button variant="outline" size="sm" className="h-8" onClick={async () => { const r = await labelsAPI.createAgent(); setPairCode(r.data.pairing_code); }} data-testid="labels-create-agent-btn">Générer un code d'appairage</Button>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/labels/agent/installer`} download data-testid="labels-installer-link">
                                <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800"><Download className="w-3.5 h-3.5 mr-1" />Installateur Windows (1 clic)</Button>
                            </a>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/labels/agent/download`} download>
                                <Button variant="outline" size="sm" className="h-8"><Download className="w-3.5 h-3.5 mr-1" />Agent seul (.py)</Button>
                            </a>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5">Recommandé : téléchargez <code>installer_sokuzai.bat</code> sur le PC Windows et double-cliquez — il installe Python/les dépendances si besoin, récupère toujours la dernière version de l'agent puis le lance. Relancez simplement ce même fichier à chaque mise à jour.</p>
                    </div>
                </div>
                {adjustTpl && (
                    <LabelAdjusterDialog clientId={client.id} template={adjustTpl} products={products} scope="template"
                        onClose={(saved) => { setAdjustTpl(null); if (saved) refreshTemplates(); }} />
                )}
            </DialogContent>
        </Dialog>
    );
}
